/**
 * StatsCard - Dashboard stat display
 */

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    label: string;
    isPositive: boolean;
  };
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
}

export function StatsCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  variant = 'default',
}: StatsCardProps) {
  const variantClasses = {
    default: 'bg-white border-warm-200',
    primary: 'bg-primary-50 border-primary-200',
    success: 'bg-success-50 border-success-200',
    warning: 'bg-warning-50 border-warning-200',
    danger: 'bg-danger-50 border-danger-200',
  };

  const iconBgClasses = {
    default: 'bg-warm-100 text-warm-600',
    primary: 'bg-primary-100 text-primary-600',
    success: 'bg-success-100 text-success-600',
    warning: 'bg-warning-100 text-warning-600',
    danger: 'bg-danger-100 text-danger-600',
  };

  return (
    <div className={`rounded-lg border p-3 sm:p-6 ${variantClasses[variant]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs sm:text-sm font-medium text-warm-500">{title}</p>
          <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-display font-bold text-warm-900">
            {value}
          </p>
          {subtitle && (
            <p className="mt-1 text-sm text-warm-500">{subtitle}</p>
          )}
          {trend && (
            <div className="mt-2 flex items-center gap-1">
              <span
                className={`text-sm font-medium ${
                  trend.isPositive ? 'text-success-600' : 'text-danger-600'
                }`}
              >
                {trend.isPositive ? '+' : '-'}
                {Math.abs(trend.value)}%
              </span>
              <span className="text-sm text-warm-500">{trend.label}</span>
            </div>
          )}
        </div>
        <div
          className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${iconBgClasses[variant]}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

export default StatsCard;
